/**
 * HubSpot analytics — thin wrapper around the HubSpot tracking code (`_hsq`).
 *
 * Uses the legacy `_hsq` queue API (works on every HubSpot tier, no
 * pre-configuration needed in HubSpot). `trackEvent` pushes a
 * `trackCustomBehavioralEvent`-shaped payload when the portal has custom
 * behavioral events configured, and always also fires the universal
 * `identify` + a same-named legacy `trackEvent` call so basic reporting
 * works immediately even without HubSpot-side event setup.
 *
 * Every user-facing action added to the app should call `track()` here —
 * see AGENTS.md / CLAUDE.md "HubSpot analytics" section for the required
 * pattern on new features.
 */

declare global {
  interface Window {
    _hsq?: unknown[];
  }
}

const PORTAL_ID = import.meta.env.VITE_HUBSPOT_PORTAL_ID as string | undefined;

let scriptLoaded = false;

function hsq(): unknown[] {
  window._hsq = window._hsq || [];
  return window._hsq;
}

/** Injects the HubSpot tracking script once. No-ops if no portal ID is configured. */
export function initAnalytics(): void {
  if (scriptLoaded || !PORTAL_ID) return;
  scriptLoaded = true;
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `//js-na1.hs-scripts.com/${PORTAL_ID}.js`;
  document.head.appendChild(script);
}

/** Call on every route change to record a page view. */
export function trackPageView(path: string): void {
  hsq().push(['setPath', path]);
  hsq().push(['trackPageView']);
}

/**
 * Records a single user action.
 * `eventName` should be a stable, snake_case identifier (e.g. "video_play",
 * "tag_add", "search_submit") — see ANALYTICS_EVENTS in this file for the
 * canonical list. `properties` is free-form context for the event
 * (asset id, query text, tag type, etc.) — kept small and non-PII.
 */
export function trackEvent(eventName: string, properties: Record<string, unknown> = {}): void {
  hsq().push([
    'trackEvent',
    {
      id: eventName,
      name: eventName,
      value: properties,
    },
  ]);
  if (import.meta.env.DEV && !PORTAL_ID) {
    // No portal configured yet (local/dev placeholder) — surface events in
    // the console so instrumentation can be verified without a live account.
    // eslint-disable-next-line no-console
    console.debug('[analytics]', eventName, properties);
  }
}

/** Identifies the current user for HubSpot contact-level tracking. */
export function identifyUser(email: string, properties: Record<string, unknown> = {}): void {
  hsq().push(['identify', { email, ...properties }]);
}

